import {
  animate,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  Component,
  computed,
  HostListener,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import type {
  DatasetOnboardingSelection,
  MetadataEntryMode,
  MetadataPathOption,
  OgcOnboardingMode,
  ResourceServerId,
  ResourceServerOption,
} from './dataset-home.models';
import {
  downloadDatasetMetadataTemplate,
  downloadDatasetMetadataTemplateCsv,
} from '../metadata-json/dataset-metadata-template.util';

@Component({
  selector: 'lib-dataset-home',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dataset-home.component.html',
  styleUrl: './dataset-home.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('dhMetaReveal', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateY(1.25rem)',
        }),
        animate(
          '420ms cubic-bezier(0.22, 1, 0.36, 1)',
          style({
            opacity: 1,
            transform: 'translateY(0)',
          }),
        ),
      ]),
    ]),
  ],
})
export class DatasetHomeComponent {
  readonly selectionComplete = output<DatasetOnboardingSelection>();

  readonly servers: ResourceServerOption[] = [
    {
      id: 'ngsi-ld',
      title: 'NGSI-LD Server',
      icon: 'network',
      description:
        'Create a dataset tailored for NGSI-LD context brokers. Supports structured entities and context data management.',
    },
    {
      id: 'file-server',
      title: 'File Server',
      icon: 'files',
      description:
        'Host datasets on the Files Connect server. Best for CSV, documents, and direct multipart file uploads.',
    },
    {
      id: 'ogc',
      title: 'OGC Server',
      icon: 'globe',
      description:
        'Create a dataset compatible with Open Geospatial Consortium standards. Ideal for spatial data infrastructures.',
    },
    {
      id: 'gateway',
      title: 'GATEWAY Server',
      icon: 'server',
      description:
        'Deploy datasets through a secure API gateway. Perfect for custom enterprise routing and advanced rate limiting.',
    },
  ];

  readonly metadataPaths: MetadataPathOption[] = [
    {
      mode: 'manual',
      title: 'Enter Manually',
      icon: 'pencil',
      description: 'Fill in the fields manually',
    },
    {
      mode: 'json-upload',
      title: 'Upload Metadata',
      icon: 'upload',
      description: 'Upload a pre-configured JSON file directly',
    },
  ];

  readonly ogcOnboardingModes: Array<{
    mode: OgcOnboardingMode;
    title: string;
    description: string;
  }> = [
    {
      mode: 'feature',
      title: 'Feature Onboarding',
      description: 'Use OGC feature-style onboarding for vector or feature collections.',
    },
    {
      mode: 'raster',
      title: 'Raster Onboarding',
      description: 'Use OGC raster-style onboarding for tiled or imagery datasets.',
    },
  ];

  readonly selectedServer = signal<ResourceServerId | null>(null);
  readonly selectedOgcOnboardingMode = signal<OgcOnboardingMode | null>(null);
  readonly metadataMode = signal<MetadataEntryMode | null>(null);
  readonly templateMenuOpen = signal(false);

  readonly canContinue = computed(
    () =>
      this.selectedServer() !== null &&
      (!this.requiresOgcOnboardingMode() || this.selectedOgcOnboardingMode() !== null) &&
      this.metadataMode() !== null,
  );

  readonly requiresOgcOnboardingMode = computed(
    () => this.selectedServer() === 'ogc',
  );

  readonly metadataSectionEnabled = computed(
    () =>
      this.selectedServer() !== null &&
      (!this.requiresOgcOnboardingMode() || this.selectedOgcOnboardingMode() !== null),
  );

  selectServer(id: ResourceServerId): void {
    if (this.isServerComingSoon(id)) return;
    const prev = this.selectedServer();
    this.selectedServer.set(id);
    if (prev !== id) {
      this.selectedOgcOnboardingMode.set(null);
      this.metadataMode.set(null);
    }
  }

  isServerComingSoon(id: ResourceServerId): boolean {
    return this.servers.find((s) => s.id === id)?.comingSoon === true;
  }

  selectMetadataMode(mode: MetadataEntryMode): void {
    if (!this.metadataSectionEnabled()) return;
    this.metadataMode.set(mode);
  }

  selectOgcOnboardingMode(mode: OgcOnboardingMode): void {
    if (!this.requiresOgcOnboardingMode()) return;
    this.selectedOgcOnboardingMode.set(mode);
    this.metadataMode.set(null);
  }

  onContinue(): void {
    const rs = this.selectedServer();
    const mode = this.metadataMode();
    const ogcMode = this.selectedOgcOnboardingMode() ?? undefined;
    if (rs === null || mode === null) return;
    if (rs === 'ogc' && ogcMode === undefined) return;
    this.selectionComplete.emit({
      resourceServer: rs,
      ogcOnboardingMode: rs === 'ogc' ? ogcMode : undefined,
      metadataMode: mode,
    });
  }

  showJsonTemplateDownload(): boolean {
    return (
      this.metadataMode() === 'json-upload' && this.selectedServer() !== null
    );
  }

  toggleTemplateMenu(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.selectedServer()) return;
    this.templateMenuOpen.update((open) => !open);
  }

  @HostListener('document:click')
  closeTemplateMenu(): void {
    if (!this.templateMenuOpen()) return;
    this.templateMenuOpen.set(false);
  }

  downloadJsonTemplate(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const rs = this.selectedServer();
    if (!rs) return;
    this.templateMenuOpen.set(false);
    downloadDatasetMetadataTemplate();
  }

  downloadCsvTemplate(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const rs = this.selectedServer();
    if (!rs) return;
    this.templateMenuOpen.set(false);
    downloadDatasetMetadataTemplateCsv();
  }
}
